import type { Lang } from "@/utils/routes";

/** The one developer page. Every developer fact lives under one of its anchors. */
export const DEVELOPER_HUB_PATHS: Record<Lang, string> = {
  sv: "/resurser/utvecklare",
  en: "/en/resources/developers",
};

/** Stable section anchors, shared by both locales and by the old-URL redirects. */
export const DEVELOPER_SECTIONS = {
  overview: "oversikt",
  start: "kom-igang",
  connect: "connect",
  mcp: "mcp",
  mcpPublic: "mcp-publik",
  mcpCustomer: "mcp-kund",
  mcpTools: "mcp-verktyg",
  api: "api",
  architecture: "arkitektur",
  versions: "versioner",
} as const;

export type DeveloperSection = keyof typeof DEVELOPER_SECTIONS;

export function developerSectionPath(
  locale: Lang,
  section: DeveloperSection,
): string {
  return `${DEVELOPER_HUB_PATHS[locale]}#${DEVELOPER_SECTIONS[section]}`;
}

/** The side-menu entries, in page order. */
export const DEVELOPER_MENU: Record<
  Lang,
  { section: DeveloperSection; label: string }[]
> = {
  sv: [
    { section: "overview", label: "Översikt" },
    { section: "architecture", label: "Arkitektur" },
    { section: "start", label: "Kom igång" },
    { section: "connect", label: "CAIRE Connect" },
    { section: "mcp", label: "MCP" },
    { section: "api", label: "API-referens" },
    { section: "versions", label: "Versioner" },
  ],
  en: [
    { section: "overview", label: "Overview" },
    { section: "architecture", label: "Architecture" },
    { section: "start", label: "Getting started" },
    { section: "connect", label: "CAIRE Connect" },
    { section: "mcp", label: "MCP" },
    { section: "api", label: "API reference" },
    { section: "versions", label: "Versions" },
  ],
};

export const PARTNER_API_OPENAPI_URL =
  "https://app.caire.se/api/partner/v1/openapi.json";

export type DeveloperSummaryCard = {
  section: DeveloperSection;
  title: string;
  summary: string;
};

/** The four summary cards under the page title. */
export const DEVELOPER_SUMMARY_CARDS: Record<Lang, DeveloperSummaryCard[]> = {
  sv: [
    {
      section: "architecture",
      title: "Arkitektur",
      summary: "En kärna, datalager och säkerhet",
    },
    {
      section: "connect",
      title: "CAIRE Connect",
      summary: "Partner-API: läs, skicka in, kvitton",
    },
    {
      section: "mcp",
      title: "MCP",
      summary: "Koppla Claude, ChatGPT eller Cursor",
    },
    {
      section: "api",
      title: "API-referens",
      summary: "OpenAPI och anrop",
    },
  ],
  en: [
    {
      section: "architecture",
      title: "Architecture",
      summary: "One core, data layers and security",
    },
    {
      section: "connect",
      title: "CAIRE Connect",
      summary: "Partner API: read, submit, receipts",
    },
    {
      section: "mcp",
      title: "MCP",
      summary: "Connect Claude, ChatGPT or Cursor",
    },
    {
      section: "api",
      title: "API reference",
      summary: "OpenAPI and operations",
    },
  ],
};

export type DeveloperQuickstartCopy = {
  title: string;
  intro: string;
  steps: string[];
  exampleLabel: string;
  openApiLabel: string;
  curlExample: string;
};

/** Placeholders only: never a real client ID, secret or token. */
function partnerApiCurlExample(tokenComment: string, readComment: string) {
  return [
    `# ${tokenComment}`,
    'curl -s -u "$CLIENT_ID:$CLIENT_SECRET" \\',
    "  -d grant_type=client_credentials \\",
    "  https://app.caire.se/api/partner/v1/oauth/token",
    "",
    `# ${readComment}`,
    'curl -s -H "Authorization: Bearer $ACCESS_TOKEN" \\',
    "  https://app.caire.se/api/partner/v1/service-areas",
  ].join("\n");
}

/** The one quickstart: administrator setup, the first read and production checks. */
export const DEVELOPER_QUICKSTART: Record<Lang, DeveloperQuickstartCopy> = {
  sv: {
    title: "Kom igång",
    intro:
      "Så gör ni den första läsningen. Ersätt platshållarna med klient-id och hemlighet för er API-nyckel.",
    steps: [
      "Öppna Inställningar → Integrationer och kontrollera att Partner-API:t är påslaget för organisationen.",
      "Skapa en API-nyckel. Välj de enheter den får använda, minsta möjliga behörigheter och bara de dataklasser som behövs.",
      "Spara hemligheten när den visas. CAIRE visar den bara en gång. Lägg den inte i ärenden, chattar eller källkod.",
      "Hämta en åtkomsttoken från tokenadressen med klient-id och hemlighet. Token gäller i högst en timme.",
      "Gör en första läsning, till exempel av de enheter nyckeln får använda. Listor läses sida för sida, och med updatedSince hämtar ni bara det som ändrats.",
      "Begäranden, frånvaroanmälningar och kvitton kräver rubriken Idempotency-Key med ett unikt värde per anrop. Samma nyckel med samma innehåll ger samma svar igen.",
      "Pröva också nekad åtkomst, upprepade anrop, en återkallad nyckel och leverans av händelser innan integrationen används i verksamheten.",
    ],
    exampleLabel: "Exempel med curl",
    openApiLabel: "API-beskrivning (OpenAPI)",
    curlExample: partnerApiCurlExample(
      "1. Hämta en åtkomsttoken",
      "2. Läs de enheter nyckeln får använda",
    ),
  },
  en: {
    title: "Getting started",
    intro:
      "This is how you make the first read. Replace the placeholders with your API key's client ID and secret.",
    steps: [
      "Open Settings → Integrations and check that the Partner API is switched on for the organisation.",
      "Create an API key. Choose the units it may use, the fewest permissions possible and only the data classes you need.",
      "Save the secret when it is shown. CAIRE shows it only once. Do not put it in tickets, chats or source code.",
      "Get an access token from the token URL with the client ID and secret. The token is valid for at most one hour.",
      "Make a first read, for example of the units the key may use. Lists are read page by page, and updatedSince returns only what has changed.",
      "Requests, absence reports and receipts require an Idempotency-Key header with a unique value per call. The same key with the same content returns the same response again.",
      "Also test denied access, repeated calls, a revoked key and event delivery before the integration is used in operations.",
    ],
    exampleLabel: "Example with curl",
    openApiLabel: "API description (OpenAPI)",
    curlExample: partnerApiCurlExample(
      "1. Get an access token",
      "2. Read the units the key may use",
    ),
  },
};

/** The one versioning section. */
export const DEVELOPER_VERSIONS: Record<
  Lang,
  { title: string; status: string; body: string[] }
> = {
  sv: {
    title: "Versioner",
    status: "v1 · Beta",
    body: [
      "Partner-API:t har version v1. Inom v1 läggs bara nya fält, värden och anrop till; ert system ska ignorera fält och värden det inte känner igen. En ändring som inte är bakåtkompatibel blir v2, och v1 stöds i minst 12 månader efter att v2 har blivit allmänt tillgänglig.",
      "Ett anrop som ska fasas ut svarar med rubrikerna Deprecation, Sunset och en länk till ersättningen innan det tas bort.",
      "Äldre adresser för Connect, MCP och API-referensen skickas vidare till sina avsnitt på den här sidan.",
    ],
  },
  en: {
    title: "Versions",
    status: "v1 · Beta",
    body: [
      "The Partner API is version v1. Within v1 only new fields, values and calls are added; your system should ignore fields and values it does not recognise. A change that is not backward compatible becomes v2, and v1 stays supported for at least 12 months after v2 is generally available.",
      "An operation that is being retired answers with Deprecation and Sunset headers and a link to its replacement before it is removed.",
      "Older addresses for Connect, MCP and the API reference forward to their sections on this page.",
    ],
  },
};
